import { numberFormatter } from './formatters';

/**
 * Percentages in the UI are numbers on a 0-100 scale (e.g. `87.35` for 87.35 %).
 * The backend often sends ratios instead (e.g. `0.8735`); convert them with
 * {@link ratioToPercent} and {@link percentToRatio}.
 */
export const PERCENT_MIN = 0;
export const PERCENT_MAX = 100;

/** Default number of fraction digits for percentages on the 0-100 scale. */
export const PERCENT_FRACTION_DIGITS = 2;

/**
 * Converts a ratio to a percentage on the 0-100 scale, rounded to
 * `fractionDigits` to drop floating-point noise.
 *
 * @example ratioToPercent(0.8735) // 87.35
 */
export function ratioToPercent(
  ratio: number,
  fractionDigits = PERCENT_FRACTION_DIGITS,
): number {
  return roundTo(ratio * 100, fractionDigits);
}

/**
 * Converts a percentage on the 0-100 scale to a ratio. The result keeps two
 * more fraction digits than the percentage, which drops floating-point noise
 * without losing precision.
 *
 * @example percentToRatio(12.3) // 0.123 (not 0.12300000000000001)
 */
export function percentToRatio(
  percent: number,
  percentFractionDigits = PERCENT_FRACTION_DIGITS,
): number {
  return roundTo(percent / 100, percentFractionDigits + 2);
}

/**
 * Creates a locale-aware formatter for percentages on the 0-100 scale,
 * e.g. `87.35` → `87,35 %` (de-DE) or `87.35%` (en-GB).
 *
 * Uses the `percent` unit instead of `style: 'percent'`, which expects a ratio.
 * Falls back like {@link numberFormatter} for missing or invalid locales.
 */
export function percentFormatter(
  locales?: string | string[],
  options: Intl.NumberFormatOptions = {},
): (percent: number) => string {
  return numberFormatter(locales, {
    maximumFractionDigits: PERCENT_FRACTION_DIGITS,
    ...options,
    style: 'unit',
    unit: 'percent',
  });
}

function roundTo(value: number, fractionDigits: number): number {
  return parseFloat(value.toFixed(fractionDigits));
}
